/**
 * Playback levels from the v3 sheet. Studio files are already at their
 * targets, so most categories play at unity on a master bus at 0 dB.
 * Music is the exception: menus stay at 0 dB. A duel sits 4 dB down on
 * the bed, then holds the 12 dB activity duck for the whole match.
 */

export function dbToGain(db: number): number {
  return 10 ** (db / 20);
}

/** Menu loops. The files are already about -16 LUFS integrated. */
export const MENU_MUSIC_DB = 0;
export const MENU_MUSIC_GAIN = dbToGain(MENU_MUSIC_DB);

/** Duel bed while the player is acting. */
export const DUEL_ACTING_DB = -4;
export const DUEL_ACTING_GAIN = dbToGain(DUEL_ACTING_DB);

/**
 * The duel bed's acting level. Menus use MENU_MUSIC_GAIN instead, so the
 * shared music bus is only the pause-sheet slider.
 */
export const MUSIC_LEVEL = DUEL_ACTING_GAIN;

/** Idle (no input for 3s) and the rival's turn, under the acting level. */
export const IDLE_DUCK_DB = -12;
export const IDLE_DUCK_GAIN = dbToGain(IDLE_DUCK_DB);
export const IDLE_AFTER_SEC = 3;
export const IDLE_ATTACK_SEC = 0.3;
export const IDLE_RELEASE_SEC = 0.8;

/** Voice lines duck the music bus. Tile chimes duck on their own bus. */
export const VOICE_MUSIC_DUCK_DB = -8;
export const VOICE_MUSIC_DUCK_GAIN = dbToGain(VOICE_MUSIC_DUCK_DB);
export const VOICE_DUCK_ATTACK_SEC = 0.04;
export const VOICE_DUCK_RELEASE_SEC = 0.4;

export const TILE_DUCK_DB = -6;
export const TILE_DUCK_GAIN = dbToGain(TILE_DUCK_DB);

/** Trick casts duck music for the animation. Match SFX stay up. */
export const CAST_MUSIC_DUCK_DB = -6;
export const CAST_MUSIC_DUCK_GAIN = dbToGain(CAST_MUSIC_DUCK_DB);
export const CAST_DUCK_ATTACK_SEC = 0.04;
export const CAST_DUCK_RELEASE_SEC = 0.2;

/** Shared cast bed is trimmed with this fade when the animation is shorter. */
export const CAST_FADE_SEC = 0.15;

/**
 * Trick clicks are hotter than match SFX. Trim inside the -4 to -6 dB band.
 * -5 dB is the middle of that band.
 */
export const TRICK_UI_TRIM_DB = -5;
export const TRICK_UI_GAIN = dbToGain(TRICK_UI_TRIM_DB);

/** Beat of silence before a turn callout. The sheet's window is 0.5–1 s. */
export const TURN_CALL_GAP_SEC = 0.75;

/** Studio casts and accents are already at level. Turn callouts carry their own trim. */
export const CAST_GAIN = 1;

export type DuelActivity = 'acting' | 'idle' | 'rival';

/** Extra music gain for the duel activity duck, and how long the move takes. */
export function activityDuck(activity: DuelActivity): { gain: number; seconds: number } {
  if (activity === 'acting') return { gain: 1, seconds: IDLE_RELEASE_SEC };
  return { gain: IDLE_DUCK_GAIN, seconds: IDLE_ATTACK_SEC };
}

/**
 * A duel keeps the 12 dB duck for the whole match. Acting does not open the
 * bed again. Menus pass `held` false, so acting still returns to unity.
 */
export function activityWhileDueling(held: boolean, activity: DuelActivity): DuelActivity {
  if (held) return 'idle';
  return activity;
}

/**
 * Voice lines do not dip the duel bed a second time. It is already at the
 * duck, and releasing that extra dip between lines would pump the loop.
 * Outside a duel the 8 dB voice duck still applies.
 */
export function voiceDucksMusic(duelHeld: boolean): boolean {
  return !duelHeld;
}

/**
 * Beat of silence before a turn callout when the speaker is free.
 * A line that is already playing drops the callout. Playback does not wait
 * out `voiceRemainingSec` and then start the callout.
 */
export function turnCallDelaySec(voiceRemainingSec: number): number {
  const remaining = Number.isFinite(voiceRemainingSec) ? Math.max(0, voiceRemainingSec) : 0;
  return remaining + TURN_CALL_GAP_SEC;
}
